import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  ShieldAlert, Sparkles, Loader2, Copy, ChevronDown, ChevronRight,
  Target, Users, FileText, Clock, Gauge, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const CONFIDENCE_CLS = {
  high:   "text-[#00F5D4] border-[#00F5D4]/30 bg-[#00F5D4]/10",
  medium: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  low:    "text-[#FF007A] border-[#FF007A]/30 bg-[#FF007A]/10",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

/**
 * RecoveryPlaybookPanel — surfaces the AI Recovery Playbook workflow for a
 * project on the Burndown screen. Lists prior playbooks, lets an owner/admin
 * generate a fresh one, and renders the structured AI output (executive
 * summary, root causes, immediate actions, resource reallocation, and a
 * client-ready scope-amendment draft).
 *
 * `driftExceeded` reflects whether the live margin has crossed the 15%
 * critical threshold — drives the urgency styling of the generate CTA.
 */
const RecoveryPlaybookPanel = ({ projectId, canGenerate = true, driftExceeded = false }) => {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/recovery-playbooks`);
      setPlaybooks(data || []);
      // Auto-expand the newest playbook so the latest plan is visible at a glance.
      if (data?.length) setExpandedId((cur) => cur || data[0].playbook_id);
    } catch (e) {
      // 404 = project not found; anything else is a soft failure for a side panel.
      if (e.response?.status !== 404) toast.error("Couldn't load recovery playbooks");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/recovery-playbook`);
      setPlaybooks((prev) => [data, ...prev]);
      setExpandedId(data.playbook_id);
      toast.success("Recovery playbook generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate playbook");
    } finally {
      setGenerating(false);
    }
  };

  const copyScopeAmendment = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Scope-amendment draft copied");
    } catch {
      toast.error("Copy failed — your browser blocked clipboard access");
    }
  };

  return (
    <div className="border border-[#1F2937] rounded-md overflow-hidden" data-testid="recovery-playbook-panel">
      {/* Header */}
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2 bg-[#111827]">
        <ShieldAlert className={`w-4 h-4 ${driftExceeded ? "text-[#FF007A]" : "text-[#00F5D4]"}`} />
        <div className="font-display font-bold tracking-tight text-sm text-white">
          Recovery Playbooks <span className="text-slate-600 font-normal">({playbooks.length})</span>
        </div>
        {canGenerate && (
          <button
            onClick={generate}
            disabled={generating}
            className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono font-semibold transition-colors disabled:opacity-50 ${
              driftExceeded
                ? "bg-[#FF007A] hover:bg-[#FF007A]/90 text-white shadow-glow-pink-md"
                : "bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-[#00F5D4]"
            }`}
            data-testid="generate-playbook-btn"
          >
            {generating
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing…</>
              : <><Sparkles className="w-3.5 h-3.5" /> Generate playbook</>
            }
          </button>
        )}
      </div>

      <div className="bg-[#111827]">
        {loading ? (
          <div className="p-8 text-center text-xs text-slate-500 font-mono">Loading playbooks…</div>
        ) : playbooks.length === 0 ? (
          <div className="p-8 text-center" data-testid="empty-playbooks">
            <Gauge className="w-6 h-6 text-slate-700 mx-auto mb-2" />
            <p className="text-xs text-slate-500 font-mono">
              No recovery playbooks yet.
              {driftExceeded
                ? " Margin has breached the 15% threshold — generate one to get AI recovery actions."
                : " AI generates these automatically when margin drifts ≥15% below target."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#1F2937]">
            {playbooks.map((pb) => (
              <PlaybookRow
                key={pb.playbook_id}
                pb={pb}
                expanded={expandedId === pb.playbook_id}
                onToggle={() => setExpandedId((cur) => (cur === pb.playbook_id ? null : pb.playbook_id))}
                onCopyScope={copyScopeAmendment}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PlaybookRow = ({ pb, expanded, onToggle, onCopyScope }) => {
  const snap = pb.margin_snapshot || {};
  const confCls = CONFIDENCE_CLS[pb.confidence] || CONFIDENCE_CLS.medium;

  return (
    <div data-testid={`playbook-${pb.playbook_id}`}>
      {/* Collapsible header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#1F2937]/30 transition-colors text-left"
        data-testid={`playbook-toggle-${pb.playbook_id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{pb.executive_summary || "Recovery playbook"}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-0.5">
            {fmtDate(pb.generated_at)}
            <span className="mx-1.5">·</span>
            <span className="text-[#FF007A]">{pb.drift_at_generation}pp drift</span>
            {pb.recovery_timeline_weeks != null && (
              <><span className="mx-1.5">·</span>{pb.recovery_timeline_weeks}wk recovery</>
            )}
          </div>
        </div>
        <span className={`text-[9px] px-2 py-1 rounded border font-mono font-bold uppercase shrink-0 ${confCls}`}>
          {pb.confidence || "—"} conf
        </span>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-5 pt-1 space-y-4" data-testid={`playbook-detail-${pb.playbook_id}`}>
          {/* Margin snapshot strip */}
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[10px] font-mono text-slate-400 bg-[#030712] border border-[#1F2937] rounded px-3 py-2">
            <span>Contract: £{Number(snap.contract_value || 0).toLocaleString()}</span>
            <span>Target: {snap.target_margin_percentage}%</span>
            <span className="text-[#FF007A] font-bold">Current: {snap.current_margin_percentage}%</span>
            <span>Cost incurred: £{Number(snap.total_cost_incurred || 0).toLocaleString()}</span>
          </div>

          {/* Root causes */}
          {pb.root_causes?.length > 0 && (
            <Section icon={Target} label="Root causes">
              <ul className="space-y-1">
                {pb.root_causes.map((c, i) => (
                  <li key={i} className="text-xs text-slate-300 flex gap-2">
                    <span className="text-[#FF007A] shrink-0">▸</span>{c}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Immediate actions */}
          {pb.immediate_actions?.length > 0 && (
            <Section icon={AlertTriangle} label="Immediate actions">
              <div className="space-y-2">
                {pb.immediate_actions.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#030712] border border-[#1F2937] rounded px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-200">{a.action}</div>
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                        Owner: <span className="text-[#00F5D4]">{a.owner || "—"}</span>
                      </div>
                    </div>
                    {a.deadline_days != null && (
                      <span className="text-[10px] font-mono text-[#F59E0B] border border-[#F59E0B]/25 bg-[#F59E0B]/10 rounded px-1.5 py-0.5 shrink-0 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {a.deadline_days}d
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Resource reallocation */}
          {pb.resource_reallocation?.length > 0 && (
            <Section icon={Users} label="Resource reallocation">
              <div className="space-y-2">
                {pb.resource_reallocation.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#030712] border border-[#1F2937] rounded px-3 py-2">
                    <div className="flex-1 min-w-0 text-xs text-slate-300">{r.recommendation}</div>
                    {r.estimated_saving_hours != null && (
                      <span className="text-[10px] font-mono text-[#00F5D4] border border-[#00F5D4]/25 bg-[#00F5D4]/10 rounded px-1.5 py-0.5 shrink-0">
                        ~{r.estimated_saving_hours}h saved
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Scope amendment draft */}
          {pb.scope_amendment_draft && (
            <Section
              icon={FileText}
              label="Scope-amendment draft"
              action={
                <button
                  onClick={() => onCopyScope(pb.scope_amendment_draft)}
                  className="flex items-center gap-1 text-[10px] font-mono text-[#00F5D4] hover:text-white transition-colors"
                  data-testid={`copy-scope-${pb.playbook_id}`}
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              }
            >
              <div className="text-xs text-slate-300 leading-relaxed bg-[#030712] border border-[#1F2937] rounded px-3 py-2.5 whitespace-pre-wrap">
                {pb.scope_amendment_draft}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, label, action, children }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono font-bold">{label}</span>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

export default RecoveryPlaybookPanel;
